/**
 * Modelo de batería de plazo en horas laborables del agente.
 * Preferir `tarea.plazo_bateria` del API (oficina − citas/permisos).
 * Fallback local: casillas = plazo CONFIGURADO (p. ej. 4 h), no ceil(Entrada→Vto).
 * Avance: solo tras horas laborables enteras (floor).
 */
import { countLaborableMinutesBetween } from "./horasLaborables.js";

const MAX_SEGMENTS = 36;
/** Alineado con backend/config/leadFlowTiming.js (evitar import circular en smokes Node). */
const DEFAULT_TASK_DUE_MINUTES = 240;

function hoursFromConfiguredPlazoMinutes(plazoMin) {
  const m = Math.max(0, Number(plazoMin) || 0);
  if (m <= 0) return 1;
  return Math.max(1, Math.round(m / 60));
}

function parseMeta(tarea) {
  let meta = tarea?.metadata;
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta || "{}");
    } catch {
      meta = {};
    }
  }
  return meta && typeof meta === "object" ? meta : {};
}

function resolveConfiguredPlazoMinutes(tarea) {
  const meta = parseMeta(tarea);
  const fromMeta = Number(
    meta.due_business_minutes ??
      meta.task_due_business_minutes ??
      meta.plazo_business_minutes
  );
  if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;
  const code = String(meta.task_code || "").trim().toUpperCase();
  if (code === "TC" || meta.agenda_end_at || meta.scheduled_end_at) return null;
  return DEFAULT_TASK_DUE_MINUTES;
}

function resolveEntradaIso(tarea = {}, entradaIso = null) {
  if (entradaIso) return entradaIso;
  return (
    tarea.fecha_inicio ||
    tarea.entrada ||
    tarea.created_at ||
    tarea.lead_created_at ||
    tarea.captacion_created_at ||
    null
  );
}

/**
 * remaining === 2 → penúltimo y último naranja.
 * remaining === 1 → solo el último rojo.
 */
export function segmentsFromTotalElapsed(total, elapsed, overdue) {
  const segments = [];
  const el = Math.min(Math.max(0, elapsed), total);
  const isOver = Boolean(overdue) || el >= total;
  const rem = isOver ? 0 : total - el;
  for (let i = 0; i < total; i++) {
    if (isOver) {
      segments.push(i === total - 1 ? "danger" : "spent");
      continue;
    }
    if (i < el) {
      segments.push("spent");
      continue;
    }
    if (rem === 1) segments.push("danger");
    else if (rem === 2) segments.push("warn");
    else segments.push("ok");
  }
  return segments;
}

function modelFromServerBateria(pb, nowMs, endMs) {
  const total = Math.min(MAX_SEGMENTS, Math.max(1, Number(pb.total) || 1));
  const overdue = Boolean(pb.overdue) || (Number.isFinite(endMs) && nowMs >= endMs);
  let elapsed = Math.min(total, Math.max(0, Number(pb.elapsed) || 0));
  if (overdue) elapsed = total;
  return {
    total,
    elapsed,
    remaining: Math.max(0, total - elapsed),
    segments: segmentsFromTotalElapsed(total, elapsed, overdue),
    overdue,
    source: "server",
  };
}

/**
 * @param {object} tarea
 * @param {number} [nowMs]
 * @param {string|null} [entradaIso]
 */
export function getTareaPlazoBateriaModel(tarea, nowMs = Date.now(), entradaIso = null) {
  try {
    if (!tarea) return null;

    const endIso = tarea.fecha_vencimiento;
    const end = endIso ? new Date(endIso).getTime() : NaN;

    if (tarea.plazo_bateria && Number(tarea.plazo_bateria.total) > 0) {
      return modelFromServerBateria(tarea.plazo_bateria, nowMs, end);
    }

    const startIso = resolveEntradaIso(tarea, entradaIso);
    if (!startIso || !endIso) return null;

    const start = new Date(startIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

    let plazoMin = resolveConfiguredPlazoMinutes(tarea);
    if (plazoMin == null) {
      const spanMin = countLaborableMinutesBetween(start, end);
      plazoMin = Math.max(60, Math.round(spanMin / 60) * 60);
    }
    const totalRaw = hoursFromConfiguredPlazoMinutes(plazoMin);
    const total = Math.min(totalRaw, MAX_SEGMENTS);
    const overdue = nowMs >= end;
    const elapsedRaw = overdue
      ? plazoMin
      : countLaborableMinutesBetween(start, Math.min(nowMs, end));
    const elapsedMin = Math.min(elapsedRaw, plazoMin);
    // Solo casilla completa tras 60 min laborables.
    let elapsed = Math.floor(elapsedMin / 60);
    if (overdue) elapsed = total;
    elapsed = Math.min(elapsed, total);

    return {
      total,
      elapsed,
      remaining: Math.max(0, total - elapsed),
      segments: segmentsFromTotalElapsed(total, elapsed, overdue),
      overdue,
      source: "office_fallback",
    };
  } catch {
    return null;
  }
}
